import {
  buildRevisionCompletionScopeKey,
  isRevisionCompletionCurrent,
} from '../../../listing-draft-payload.js';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function cleanText(value) {
  return String(value || '').trim();
}

const MARKETPLACE_LABELS = {
  US: '美国站（英语）',
  CA: '加拿大站（英语）',
  UK: '英国站（英语）',
  AU: '澳大利亚站（英语）',
  DE: '德国站（德语）',
  FR: '法国站（法语）',
  IT: '意大利站（意大利语）',
  ES: '西班牙站（西班牙语）',
  JP: '日本站（日语）',
};

function resolveMarketplace(input = {}) {
  const generationMeta = input.listingGenerationMeta || null;
  const candidates = [
    input.marketplace,
    input.productInfo?.marketplace,
    input.productInfo?.targetMarket,
    input.businessContext?.marketplace,
    generationMeta?.marketplace,
    generationMeta?.mergeMeta?.marketplace,
  ];
  return candidates
    .map((value) => cleanText(value).toUpperCase())
    .find((value) => /^[A-Z]{2}$/.test(value)) || '';
}

function normalizeSeverity(value, fallback = 'info') {
  const text = cleanText(value).toLowerCase();
  if (['critical', 'error', 'danger', 'high'].some((item) => text.includes(item))) return 'critical';
  if (['warning', 'warn', 'medium'].some((item) => text.includes(item))) return 'warning';
  if (text === 'info' || text === 'low') return 'info';
  return fallback;
}

function normalizeField(item) {
  return cleanText(item?.field || item?.section || item?.dimension || item?.targetField);
}

function normalizeMessage(item) {
  return cleanText(item?.message || item?.issue || item?.description || item?.title || item?.suggestion || item?.recommendedAction);
}

function canonicalIssueFamily(item = {}) {
  const raw = [
    item.rootCause,
    item.type,
    item.term,
    item.message,
    item.issue,
    item.suggestion,
    item.recommendedAction,
    item.evidence?.type,
    item.evidence?.rule,
    item.evidence?.check,
  ].map(cleanText).join(' ').toLowerCase();
  if (/duplicate|repetition|重复|反复/.test(raw)) return 'repetition';
  if (/trademark|brand term|商标|品牌词/.test(raw)) return 'trademark';
  if (/byte|字符超限|字节超限/.test(raw)) return 'length-limit';
  if (/placeholder|占位符/.test(raw)) return 'placeholder';
  if (/keyword.*missing|missing.*keyword|关键词.*缺失|缺少.*关键词/.test(raw)) return `keyword:${cleanText(item.evidence?.keyword || item.keyword).toLowerCase() || 'missing'}`;
  return cleanText(item.rootCause || item.type || item.evidence?.rule || item.id || normalizeMessage(item)).toLowerCase();
}

function issueKeys(item = {}) {
  const self = `${normalizeField(item) || 'general'}::${canonicalIssueFamily(item)}`;
  const childKeys = asArray(item.items).map((child) => `${normalizeField(child) || normalizeField(item) || 'general'}::${canonicalIssueFamily(child)}`);
  return [...new Set((childKeys.length ? childKeys : [self]).filter(Boolean))];
}

function attachIssueKeys(item) {
  return { ...item, dedupeKeys: issueKeys(item) };
}

function normalizeExecutionMeta(item = {}) {
  return {
    businessImpact: cleanText(item.businessImpact),
    blocksWriteback: item.blocksWriteback === true,
    evidenceRequired: item.evidenceRequired === true,
    decisionAuthority: cleanText(item.decisionAuthority),
    revisionAction: cleanText(item.revisionAction),
    changeClass: cleanText(item.changeClass),
    level: cleanText(item.level),
  };
}

function normalizeComplianceItems(complianceResult) {
  return asArray(complianceResult?.violations || complianceResult?.items || complianceResult?.issues)
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => attachIssueKeys({
      id: cleanText(item.id) || `compliance-${index}`,
      field: normalizeField(item) || 'title',
      severity: normalizeSeverity(item.severity, 'warning'),
      message: normalizeMessage(item) || '合规检测发现风险。',
      suggestion: cleanText(item.suggestion || item.recommendedAction),
      term: cleanText(item.term),
      rootCause: cleanText(item.rootCause || item.type),
      type: cleanText(item.type),
      evidence: item.evidence || null,
      ...normalizeExecutionMeta(item),
    }));
}

function normalizeScoringItems(scoringReport) {
  return asArray(scoringReport?.suggestions || scoringReport?.items || scoringReport?.issues)
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => attachIssueKeys({
      id: cleanText(item.id) || `scoring-${index}`,
      field: normalizeField(item) || 'description',
      severity: normalizeSeverity(item.priority || item.severity, 'info'),
      dimension: cleanText(item.dimension),
      message: normalizeMessage(item) || '评分模型给出优化建议。',
      suggestion: cleanText(item.description || item.recommendedAction || item.title || item.message || item.issue),
      rootCause: cleanText(item.rootCause),
      type: cleanText(item.type),
      evidence: item.evidence || null,
      ...normalizeExecutionMeta(item),
    }));
}

function normalizeRevisionItems(revisionSuggestions) {
  return asArray(revisionSuggestions?.suggestions || revisionSuggestions?.items || revisionSuggestions?.issues || revisionSuggestions)
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => attachIssueKeys({
      id: cleanText(item.id) || `revision-${index}`,
      field: normalizeField(item) || 'title',
      severity: normalizeSeverity(item.priority || item.severity, 'warning'),
      dimension: cleanText(item.dimension),
      message: normalizeMessage(item) || '需要继续优化该字段。',
      suggestion: cleanText(item.recommendedAction || item.suggestion || item.message || item.issue) || '根据诊断建议继续优化。',
      rootCause: cleanText(item.rootCause),
      type: cleanText(item.type),
      evidence: item.evidence || null,
      items: asArray(item.items),
      ...normalizeExecutionMeta(item),
    }));
}

function buildIssuePresentation(item = {}, marketplace = '') {
  const field = normalizeField(item).toLowerCase() || 'general';
  const raw = [
    item.message,
    item.suggestion,
    item.rootCause,
    item.type,
    item.revisionAction,
  ].map(cleanText).join(' ');
  const targetLabel = MARKETPLACE_LABELS[marketplace] || (marketplace ? `${marketplace} 站` : '目标站点');
  const referencesUs = /amazon\s*us|美国站|美国买家|英语内容|translate.+english|译为.+英语/i.test(raw);
  const marketMismatch = marketplace && marketplace !== 'US' && referencesUs;

  if (marketMismatch) {
    return {
      displayMessage: `诊断引用了错误站点，当前 Listing 实际属于${targetLabel}。`,
      actionSummary: `按${targetLabel}重新诊断；系统不会执行这条跨站点建议。`,
      marketMismatch: true,
    };
  }

  if (/confirm_product_facts|facts_required|商品事实|证据/.test(raw)) {
    return {
      displayMessage: '部分商品事实缺少可靠依据。',
      actionSummary: '先补充或确认商品事实，再生成优化稿。',
      marketMismatch: false,
    };
  }

  const actionByField = {
    title: '精简并重组标题，保留核心商品词与已确认事实。',
    bullets: '合并重复卖点，按“特性—利益点”重写五点。',
    description: '重组描述层次，删除重复或缺少依据的表达。',
    searchterms: '清理重复、受限和低价值词，并重新排序搜索词。',
    qa: '校正问答中的商品事实，删除无法验证的承诺。',
    aplus: '重组 A+ 模块文案，只保留可验证的卖点。',
    general: `按${targetLabel}语言重组全文，减少重复并保留商品事实。`,
    '通用': `按${targetLabel}语言重组全文，减少重复并保留商品事实。`,
  };
  const issueByFamily = {
    repetition: '内容存在重复表达或关键词堆叠。',
    trademark: '文案中存在品牌或商标使用风险。',
    'length-limit': '字段长度超出平台限制。',
    placeholder: '文案中仍有未替换的占位内容。',
  };
  const family = canonicalIssueFamily(item);
  return {
    displayMessage: issueByFamily[family]
      || (item.type === 'draft_text_review'
        ? '全文表达需要按目标站点重新整理。'
        : cleanText(item.message) || '当前 Listing 存在需要处理的问题。'),
    actionSummary: actionByField[field] || actionByField.general,
    marketMismatch: false,
  };
}

function dedupeDiagnosticSections(complianceItems, scoringItems, revisionItems) {
  const seen = new Set();
  const keepNewRoots = (items) => items.filter((item) => {
    const keys = asArray(item.dedupeKeys);
    const unseen = keys.filter((key) => !seen.has(key));
    if (!unseen.length) return false;
    keys.forEach((key) => seen.add(key));
    return true;
  });
  return {
    complianceItems: keepNewRoots(complianceItems),
    scoringItems: keepNewRoots(scoringItems),
    revisionItems: keepNewRoots(revisionItems),
  };
}

function normalizeCurrentListing({
  listingDraftTitle,
  listingDraftBullets,
  listingDraftDescription,
  listingDraftSearchTerms,
  listingDraftQa,
} = {}) {
  return {
    title: cleanText(listingDraftTitle?.titleEn || listingDraftTitle?.title || listingDraftTitle?.text),
    bullets: asArray(listingDraftBullets?.bulletsEn).filter(Boolean),
    description: cleanText(listingDraftDescription?.descriptionEn || listingDraftDescription?.description),
    searchTerms: cleanText(listingDraftSearchTerms?.searchTermsEn || listingDraftSearchTerms?.searchTerms),
    qa: asArray(listingDraftQa).filter((item) => item && typeof item === 'object'),
  };
}

function resolveStep04AuditConclusion(revisionSuggestionsMeta = null) {
  const value = cleanText(revisionSuggestionsMeta?.executionAuthority?.conclusion).toLowerCase();
  return ['pass', 'needs_revision', 'facts_required'].includes(value) ? value : '';
}

function resolveStep04CopyQuality(revisionSuggestionsMeta = null, conclusion = '') {
  const source = revisionSuggestionsMeta?.copyQuality
    || revisionSuggestionsMeta?.executionAuthority?.quality
    || null;
  if (!source || typeof source !== 'object') {
    return {
      status: conclusion === 'facts_required' ? 'facts_required' : 'unavailable',
      score: null,
      dimensions: [],
      reasons: [],
    };
  }
  const score = Number(source.score);
  return {
    status: cleanText(source.status) || (conclusion === 'facts_required' ? 'facts_required' : 'unavailable'),
    score: Number.isInteger(score) && score >= 0 && score <= 100 ? score : null,
    dimensions: asArray(source.dimensions)
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const dimensionScore = Number(item.score);
        const maxScore = Number(item.maxScore);
        return {
          key: cleanText(item.key),
          label: cleanText(item.label),
          score: Number.isInteger(dimensionScore) ? dimensionScore : null,
          maxScore: Number.isInteger(maxScore) ? maxScore : null,
        };
      })
      .filter((item) => item.label),
    reasons: asArray(source.reasons).map(cleanText).filter(Boolean).slice(0, 8),
  };
}

function deriveSummary({
  complianceResult,
  scoringReport,
  complianceItems,
  scoringItems,
  revisionItems,
  marketplace,
  step04Conclusion = '',
  copyQuality = null,
}) {
  const hasComplianceResult = Boolean(
    complianceResult
    && typeof complianceResult === 'object'
    && complianceResult.availability !== 'unavailable',
  );
  const hasScoringReport = Boolean(scoringReport && typeof scoringReport === 'object');
  const hasStep04Audit = Boolean(step04Conclusion);
  const complianceScore = Number(complianceResult?.score ?? 0) || 0;
  const scoringScore = Number(scoringReport?.totalScore ?? scoringReport?.score ?? 0) || 0;
  const issueCount = complianceItems.length + scoringItems.length + revisionItems.length;
  const qualityGate = scoringReport?.qualityGate || null;
  const compositeScore = qualityGate?.compositeScore || null;
  const copyQualityScore = Number.isInteger(copyQuality?.score) ? copyQuality.score : null;
  const publishStatus = hasStep04Audit
    ? ({ pass: 'ready', needs_revision: 'needs_changes', facts_required: 'facts_required' }[step04Conclusion] || '')
    : cleanText(qualityGate?.publishReadiness?.status);
  const statusLabels = {
    ready: '可上架',
    ready_with_improvements: '可上架，可继续优化',
    needs_changes: '修改后可上架',
    blocked: '暂不可上架',
    review_pending: '语义质量待评估',
    facts_required: '产品事实待核验',
  };

  return {
    hasDiagnosisData: hasComplianceResult || hasScoringReport || hasStep04Audit,
    complianceEvaluated: hasComplianceResult,
    scoringEvaluated: hasScoringReport && (
      scoringReport?.totalScore != null
      || scoringReport?.score != null
      || scoringReport?.qualityGate?.compositeScore?.score != null
    ),
    complianceScore,
    complianceIssueCount: complianceItems.length,
    complianceStatusLabel: hasStep04Audit
      ? '审核 Agent 已完成'
      : hasComplianceResult
      ? (complianceItems.length ? `${complianceItems.length} 项风险` : '通过')
      : '待评估',
    scoringScore,
    compositeScore: compositeScore?.score ?? null,
    compositeStatus: cleanText(compositeScore?.status),
    compositeFormula: cleanText(compositeScore?.formula),
    compositeDimensions: asArray(compositeScore?.dimensions),
    issueCount,
    publishStatus,
    publishStatusLabel: statusLabels[publishStatus] || '',
    publishBlockerCount: Math.max(
      asArray(qualityGate?.publishReadiness?.blockers).length,
      step04Conclusion === 'facts_required' ? 1 : 0,
    ),
    requiredChangeCount: Math.max(
      asArray(qualityGate?.publishReadiness?.requiredChanges).length,
      step04Conclusion === 'needs_revision' ? 1 : 0,
    ),
    contentQualityScore: qualityGate?.contentQuality?.score ?? null,
    copyQualityStatus: cleanText(copyQuality?.status) || 'unavailable',
    copyQualityScore,
    copyQualityDimensions: asArray(copyQuality?.dimensions),
    copyQualityReasons: asArray(copyQuality?.reasons),
    seoExecutionScore: qualityGate?.seoExecution?.score ?? null,
    seoExecutionStatus: cleanText(qualityGate?.seoExecution?.status),
    semanticReviewStatus: cleanText(qualityGate?.contentQuality?.semanticReviewStatus),
    semanticQualityScore: scoringReport?.semanticReview?.overallScore ?? null,
    semanticQualitySummary: cleanText(scoringReport?.semanticReview?.summary),
    optimizationSummary: cleanText(qualityGate?.optimizationPotential?.summary),
    marketplace,
    marketplaceLabel: MARKETPLACE_LABELS[marketplace] || (marketplace ? `${marketplace} 站` : '站点待确认'),
    statusLabel: statusLabels[publishStatus] || (issueCount > 0 ? '需要优化' : '状态良好'),
  };
}

function isRequiredIssue(item = {}) {
  const authority = cleanText(item.decisionAuthority).toLowerCase();
  const level = cleanText(item.level).toLowerCase();
  const changeClass = cleanText(item.changeClass).toLowerCase();
  return item.blocksWriteback === true
    || authority === 'deterministic_hard_gate'
    || level === 'hard_gate'
    || level === 'required_revision'
    || changeClass === 'required'
    || item.severity === 'critical';
}

function dedupeItems(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const keys = asArray(item.dedupeKeys);
    const unseen = keys.filter((key) => !seen.has(key));
    if (!unseen.length) return false;
    keys.forEach((key) => seen.add(key));
    return true;
  });
}

function overviewRiskToIssue(risk = {}) {
  return attachIssueKeys({
    id: 'overview-primary-risk',
    field: cleanText(risk.field) || '通用',
    severity: normalizeSeverity(risk.severity, 'warning'),
    message: cleanText(risk.issue) || '当前 Listing 存在需要优先处理的问题。',
    suggestion: cleanText(risk.recommendedAction) || '根据诊断建议生成优化稿并检查差异。',
    businessImpact: cleanText(risk.impactSummary),
    blocksWriteback: false,
    evidenceRequired: false,
    decisionAuthority: '',
    revisionAction: '',
    changeClass: 'required',
    level: 'required_revision',
    evidence: null,
    items: [],
  });
}

function buildDecisionSummary({ summary, revisionOverview, allItems, revisionCompletion = null }) {
  if (revisionCompletion) {
    return {
      title: '本轮改稿已复核完成',
      description: '当前文案已完成本轮改稿与复检。若你修改了文案或需要再次检查，请点击顶部“重新诊断”。',
      tone: 'is-ready',
      requiredCount: 0,
      optionalCount: 0,
      qualityScore: null,
      publishDecisionReady: true,
      requiredIssues: [],
      planSteps: [
        '查看本次已应用的改稿',
        '需要时点击顶部“重新诊断”',
      ],
    };
  }
  if (!summary.hasDiagnosisData) {
    return {
      title: '等待诊断',
      description: '诊断完成后，这里会明确说明当前 Listing 是否可以上架。',
      tone: 'is-pending',
      requiredCount: 0,
      optionalCount: 0,
      qualityScore: null,
      publishDecisionReady: false,
      requiredIssues: [],
      planSteps: [
        '识别影响上架的问题',
        '区分必须修改与建议优化',
        '生成安全可执行的优化方案',
      ],
    };
  }
  const publishStatus = summary.publishStatus;
  if (!publishStatus) {
    return {
      title: '等待完整诊断',
      description: '当前只有部分检查结果，尚不能判断是否可以上架。请重新诊断。',
      tone: 'is-pending',
      requiredCount: 0,
      optionalCount: summary.issueCount,
      qualityScore: null,
      publishDecisionReady: false,
      requiredIssues: [],
      planSteps: [
        '完成规则与语义检查',
        '生成唯一综合质量分',
        '给出明确上架结论',
      ],
    };
  }
  const requiresChanges = ['needs_changes', 'blocked', 'facts_required'].includes(publishStatus);
  const explicitRequiredItems = dedupeItems(allItems.filter(isRequiredIssue));
  const gateRequiredCount = summary.publishBlockerCount + summary.requiredChangeCount;
  const fallbackRisk = asArray(revisionOverview?.topRisks)[0];
  const firstVisibleIssue = allItems[0] || null;
  let requiredIssues = explicitRequiredItems;

  if (!requiredIssues.length && requiresChanges) {
    if (publishStatus === 'facts_required') {
      requiredIssues = [attachIssueKeys({
        id: 'facts-required',
        field: '商品事实',
        severity: 'critical',
        message: '部分商品事实尚未获得可靠证据，暂不能安全执行改稿。',
        suggestion: '先补充或确认商品事实，再重新诊断并生成优化稿。',
        businessImpact: 'publishability',
        blocksWriteback: true,
        evidenceRequired: true,
        decisionAuthority: 'deterministic_hard_gate',
        revisionAction: 'confirm_product_facts',
        changeClass: 'required',
        level: 'hard_gate',
        evidence: null,
        items: [],
      })];
    } else if (fallbackRisk) {
      requiredIssues = [overviewRiskToIssue(fallbackRisk)];
    } else if (firstVisibleIssue) {
      requiredIssues = [{ ...firstVisibleIssue, changeClass: 'required', level: 'required_revision' }];
    }
  }

  const statusMinimum = requiresChanges ? 1 : 0;
  const requiredCount = Math.max(explicitRequiredItems.length, gateRequiredCount, statusMinimum);
  const optionalCount = Math.max(summary.issueCount - requiredCount, 0);
  const descriptions = {
    ready: '当前没有阻止上架的问题，可直接进入发布流程。',
    ready_with_improvements: `当前可上架，另有 ${optionalCount} 项建议优化，可按需处理。`,
    needs_changes: `当前有 ${requiredCount} 项必须修改，${optionalCount} 项建议优化。处理完成后可进入发布流程。`,
    blocked: `当前有 ${requiredCount} 项问题会阻止上架，请处理后重新诊断。`,
    facts_required: '需要先确认产品事实，确认完成后再判断是否可上架。',
    review_pending: '关键质量维度尚未完成评估，暂不建议直接上架。',
  };
  const tones = {
    ready: 'is-ready',
    ready_with_improvements: 'is-ready',
    needs_changes: 'needs-changes',
    blocked: 'is-blocked',
    facts_required: 'is-blocked',
    review_pending: 'is-pending',
  };

  return {
    title: summary.publishStatusLabel || summary.statusLabel || '等待诊断',
    description: descriptions[publishStatus] || '诊断数据尚不完整，请重新诊断后再判断是否可上架。',
    tone: tones[publishStatus] || 'is-pending',
    requiredCount,
    optionalCount,
    // The old composite/scoring numbers do not contain the Step04 copy-quality
    // dimensions and historically defaulted to 0. Never relabel them as the
    // new copy score; wait for the tagged reviewer instead.
    qualityScore: summary.copyQualityScore,
    publishDecisionReady: true,
    requiredIssues: requiredIssues.map((item) => ({
      ...item,
      ...buildIssuePresentation(item, summary.marketplace),
    })),
    planSteps: [
      '按 Product Truth 校验商品身份和核心事实',
      '只处理模型明确指出的字段并生成候选稿',
      '保留原稿，确认后才写回',
    ],
  };
}

export function buildDiagnosticViewModel(input = {}) {
  const marketplace = resolveMarketplace(input);
  const revisionSuggestionsError = cleanText(input.revisionSuggestionsError);
  const rawComplianceItems = normalizeComplianceItems(input.complianceResult);
  const rawScoringItems = normalizeScoringItems(input.scoringReport);
  const rawRevisionItems = normalizeRevisionItems(input.revisionSuggestions);
  const normalized = dedupeDiagnosticSections(
    rawComplianceItems,
    rawScoringItems,
    rawRevisionItems,
  );
  const { complianceItems, scoringItems, revisionItems } = normalized;
  const currentListing = normalizeCurrentListing(input);
  const generationMeta = input.listingGenerationMeta || null;
  const productInfo = input.productInfo || generationMeta?.mergeMeta?.businessMeta?.productInfo || null;
  const keywordUsePolicies = input.keywordUsePolicies === undefined
    ? (input.businessContext?.keywordUsePolicies || [])
    : input.keywordUsePolicies;
  const competitorRoles = input.competitorRoles === undefined
    ? (input.businessContext?.competitorRoles || [])
    : input.competitorRoles;
  const revisionCompletionScopeKey = buildRevisionCompletionScopeKey({
    listing: {
      title: input.listingDraftTitle,
      bullets: input.listingDraftBullets,
      description: input.listingDraftDescription,
      searchTerms: input.listingDraftSearchTerms,
      qa: input.listingDraftQa,
    },
    marketplace,
    productInfo,
    keywordUsePolicies,
    competitorRoles,
  });
  const revisionCompletion = isRevisionCompletionCurrent(
    generationMeta?.mergeMeta?.revisionCompletion,
    revisionCompletionScopeKey,
  ) ? generationMeta.mergeMeta.revisionCompletion : null;
  const step04Conclusion = resolveStep04AuditConclusion(input.revisionSuggestionsMeta);
  const copyQuality = resolveStep04CopyQuality(input.revisionSuggestionsMeta, step04Conclusion);
  const summary = deriveSummary({
    complianceResult: input.complianceResult,
    scoringReport: input.scoringReport,
    complianceItems,
    scoringItems,
    revisionItems,
    marketplace,
    step04Conclusion,
    copyQuality,
  });
  const decision = buildDecisionSummary({
    summary,
    revisionOverview: input.revisionOverview || null,
    allItems: [...rawComplianceItems, ...rawScoringItems, ...rawRevisionItems],
    revisionCompletion,
  });
  // Facade calls update individual diagnostic slices as they return.  Those
  // partial values must not be presented as a final Step04 conclusion.
  const isDiagnosisPending = input.diagnosisInFlight === true || decision.publishDecisionReady !== true;

  return {
    diagnosisInFlight: input.diagnosisInFlight === true,
    isDiagnosisPending,
    hasAnySignals:
      complianceItems.length > 0
      || scoringItems.length > 0
      || revisionItems.length > 0
      || !!revisionSuggestionsError
      || !!currentListing.title
      || currentListing.bullets.length > 0
      || !!currentListing.description
      || !!currentListing.searchTerms
      || currentListing.qa.length > 0,
    summary,
    decision,
    sections: {
      compliance: {
        items: complianceItems,
      },
      scoring: {
        items: scoringItems,
      },
      revision: {
        items: revisionItems,
        overview: input.revisionOverview || null,
        error: revisionSuggestionsError,
      },
    },
    currentListing,
  };
}
